/**
 * Director RiskBandPanel — RFC-005 peak-period 104-pt scoring.
 *
 * Shows top-N pending leaves with their risk score, band, and
 * peak-window modifier. Consumes `GET /api/v1/insights/pattern-signals`
 * (existing endpoint) which the Director already has access to (HR+).
 *
 * Director view differs from the Manager panel: it shows the
 * aggregated totals org-wide (Director sees all via RLS) and surfaces
 * the stacked-bar + top-line counts so Rajesh can spot inflection at a
 * glance without drilling into individual requests.
 *
 * Props: items[] passed from the parent page (fetched via team-insights
 * API), so the panel is purely presentational and testable without a
 * network context.
 */
import type { CSSProperties } from 'react'
import { HealthBar, type RiskBand } from './HealthBar'
import { Card } from '../primitives/Card'

export interface RiskBandItem {
  leaveId: string
  employee: string
  riskScore: number
  riskBand: RiskBand
  /** Peak-period modifier applied: HIGH=-15, MEDIUM=-8, LOW=0 */
  modifier: number
}

export interface RiskBandPanelProps {
  items: RiskBandItem[]
  /** When true, renders a skeleton placeholder. */
  loading?: boolean
}

/**
 * Org-wide risk-band overview for the Director.
 *
 * Lists the highest-scoring pending leaves with their band + modifier.
 * Empty state is a quiet "all clear" message — no × button, panel verdict-language-ok
 * self-hides when items is empty.
 */
export function RiskBandPanel({ items, loading = false }: RiskBandPanelProps): JSX.Element | null {
  if (!loading && items.length === 0) return null

  // Sort HIGH → MEDIUM → LOW, then by descending score within band.
  const BAND_ORDER: Record<RiskBand, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 }
  const sorted = [...items].sort(
    (a, b) =>
      BAND_ORDER[a.riskBand] - BAND_ORDER[b.riskBand] ||
      b.riskScore - a.riskScore,
  )

  const highCount = items.filter((i) => i.riskBand === 'HIGH').length
  const mediumCount = items.filter((i) => i.riskBand === 'MEDIUM').length
  const lowCount = items.filter((i) => i.riskBand === 'LOW').length

  return (
    <Card padding="14px 18px" data-testid="director-risk-band-panel">
      <div style={headerStyle}>
        <h2 style={titleStyle}>Risk-band overview</h2>
        <div style={countRowStyle}>
          <BandCount label="High" count={highCount} colour="var(--danger)" />
          <BandCount label="Med" count={mediumCount} colour="var(--warning)" />
          <BandCount label="Low" count={lowCount} colour="var(--success)" />
        </div>
      </div>

      {loading ? (
        <div style={emptyStyle}>Loading…</div>
      ) : (
        <ul role="list" style={listStyle}>
          {sorted.map((item) => (
            <li
              key={item.leaveId}
              data-testid="director-risk-band-row"
              style={rowStyle}
            >
              <div style={rowNameStyle}>
                <span style={employeeStyle}>{item.employee}</span>
                <BandChip band={item.riskBand} />
              </div>
              <div style={barColStyle}>
                <HealthBar
                  score={item.riskScore}
                  band={item.riskBand}
                  showModifier={item.riskBand !== 'LOW'}
                  testId={`director-risk-bar-${item.leaveId}`}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function BandCount({ label, count, colour }: { label: string; count: number; colour: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-2)' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: colour, display: 'inline-block' }} />
      <span style={{ fontWeight: 700, color: colour }}>{count}</span>
      <span>{label}</span>
    </span>
  )
}

function BandChip({ band }: { band: RiskBand }): JSX.Element {
  const CHIP: Record<RiskBand, { bg: string; fg: string }> = {
    HIGH: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' },
    MEDIUM: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' },
    LOW: { bg: 'var(--surface-2)', fg: 'var(--text-3)' },
  }
  const { bg, fg } = CHIP[band]
  return (
    <span
      data-testid="director-band-chip"
      data-band={band}
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        padding: '2px 7px',
        borderRadius: 999,
        background: bg,
        color: fg,
      }}
    >
      {band}
    </span>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 10,
  flexWrap: 'wrap',
  gap: 8,
}

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  margin: 0,
}

const countRowStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
}

const listStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
}

const rowStyle: CSSProperties = {
  padding: '10px 0',
  borderBottom: '1px solid var(--line-subtle)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const rowNameStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}

const employeeStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
}

const barColStyle: CSSProperties = {
  paddingRight: 4,
}

const emptyStyle: CSSProperties = {
  padding: '20px 0',
  textAlign: 'center',
  color: 'var(--text-3)',
  fontSize: 13,
}
